import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Users as UsersIcon, MessageSquare, FileText, ArrowRight, LayoutGrid } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { api } from "@/lib/api";
import { getServiceIcon, getAccent } from "@/lib/serviceIcons";

export default function Workspaces() {
  const { profile } = useApp();
  const navigate = useNavigate();
  const [workspaces, setWorkspaces] = useState([]);

  useEffect(() => { if (profile) api.getWorkspaces(profile.id).then(setWorkspaces).catch(() => {}); }, [profile]);

  return (
    <div className="h-full overflow-y-auto p-6 md:p-10 pb-24 relative hide-scrollbar">
      <div className="max-w-6xl mx-auto space-y-8 fade-in relative z-10">
        <div>
          <h2 className="text-3xl font-bold text-white tracking-tight font-display">My Workspaces</h2>
          <p className="text-sm text-zinc-400 mt-1">Each active project has a dedicated workspace to collaborate with your DONDRÁE team.</p>
        </div>

        {workspaces.length === 0 ? (
          <div className="glass-panel p-16 rounded-2xl text-center flex flex-col items-center justify-center border-dashed border-2 border-white/10">
            <div className="w-20 h-20 bg-white/5 rounded-full flex items-center justify-center mb-6 border border-white/10"><LayoutGrid size={32} className="text-zinc-500" /></div>
            <h3 className="text-xl font-bold text-white">No workspaces yet</h3>
            <p className="text-zinc-400 text-sm mt-2 max-w-sm">Purchase a service from the directory to open a collaborative project workspace here.</p>
            <button onClick={() => navigate("/app/directory")} className="mt-6 px-5 py-2.5 bg-primary-600 text-white rounded-xl text-sm font-bold hover:bg-primary-500 transition glow-box flex items-center">Browse Services <ArrowRight size={15} className="ml-2" /></button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {workspaces.map((w) => {
              const Icon = getServiceIcon(w.icon); const accent = getAccent(w.accent);
              const done = w.current_stage_index >= w.stages.length - 1;
              const pct = Math.round((w.current_stage_index / (w.stages.length - 1)) * 100);
              return (
                <button key={w.id} onClick={() => navigate(`/app/workspace/${w.id}`)} className="text-left glass-panel rounded-2xl p-6 hover:border-white/20 transition-colors group">
                  <div className="flex items-center gap-4 mb-5">
                    <div className={`w-12 h-12 rounded-xl border flex items-center justify-center ${accent.bg} ${accent.text} ${accent.border}`}><Icon size={22} /></div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-bold text-white truncate group-hover:text-primary-300 transition-colors">{w.title}</h3>
                      <p className="text-xs text-zinc-500">{done ? "Completed" : `Current stage: ${w.stages[w.current_stage_index]}`}</p>
                    </div>
                    <ArrowRight size={18} className="text-zinc-600 group-hover:text-white transition-colors" />
                  </div>
                  <div className="w-full h-1.5 rounded-full bg-white/10 mb-4 overflow-hidden">
                    <div className={`h-full rounded-full ${done ? "bg-green-500" : "bg-primary-500"}`} style={{ width: `${pct}%` }} />
                  </div>
                  <div className="flex items-center gap-4 text-xs text-zinc-400">
                    <span className="flex items-center gap-1.5"><MessageSquare size={13} /> {w.message_count} messages</span>
                    <span className="flex items-center gap-1.5"><FileText size={13} /> {w.deliverable_count} files</span>
                    {w.total ? <span className="ml-auto text-white font-bold">${w.total.toLocaleString()}</span> : null}
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
